/**
 * Bank Statement Transactions, the screen's logic without React (IP-58,
 * IP-67..71, IP-111, IP-112; design 2026-10-02 sections 6 "Cash" and 8).
 * Every figure is the server's: GET cash/lines answers each line as
 * receipts.js mapLine plus the IP-112 fields (transactionRef,
 * openMatchAmount, proposedBy / proposedByName, noteCount, lastNote) and, on
 * an unallocated credit, its age (ageBusinessDays, stage, refundable - or
 * ageUnknown 'NO_FUND_CONFIG' when its fund has no fund_config today). The
 * page decides only what to OFFER; the server judges every action again
 * (LINE_NOT_IN_CLEARING, LINE_NOT_UNALLOCATED, NOT_CLEARED, MAKER_IS_CHECKER,
 * FUND_NOT_CONFIGURED...).
 *
 * What a line offers (design 8, "Action"):
 *
 *   Approve / Reject   MANUAL_PENDING, its match open, to anyone but the
 *                      person who claimed it (proposedBy; the server refuses
 *                      the claimant - spec 6.1, leg 2 != leg 1)
 *   Clear              a credit AWAITING_CLEARANCE: the bank's realisation
 *                      date and reference (LINE_CLEAR, Ruling R19a)
 *   Return             a credit AWAITING_CLEARANCE that is also matched (AUTO
 *                      or MANUAL_APPROVED), or unallocated (UNRECONCILED, no
 *                      claim open - never matched, or released by a reject or
 *                      a lapse, IP-130): a dishonoured cheque (T8)
 *   Refund             refundable - an unallocated credit the bank has
 *                      cleared (Ruling R19e, D-4); offered as Clear
 *   Unreconcile        an AUTO or approved MANUAL match until its transaction
 *                      is approved (IP-196) - the server's own rule, unreconcileBlockedBy
 *                      on the line, null when it may still be undone; with a
 *                      comment (LINE_UNRECONCILE, IP-138). A final match
 *                      (its order FROZEN or beyond, or its book closed) is
 *                      not offered at all
 *   Clear (by hand)    any other left-over line, either direction - unmatched,
 *                      no claim open, neither returned nor in clearing - with
 *                      a reason (LINE_MANUAL_CLEAR, IP-157): a debit nothing
 *                      matched, a credit with no refund to offer
 *
 * and an open credit (CRDT, UNRECONCILED, not RETURNED - matching.js
 * openLine) with no open match is claimed from its candidates (MATCH_CLAIM).
 * Each of those eight is step-up (IP-111): ACTIONS below names the catalogue
 * type, the params its code is bound to (catalogue.js - exactly what the
 * server derives from the body and the path) and the call that sends them.
 * A note (LINE_NOTE) is a session action: a note is not a decision.
 *
 * IP-176 (design 2026-10-13 section 2): one subscription may be paid by
 * several credits, each a part. A candidate says what it is still owed and
 * how much of it is in or claimed (receivedAmount, pendingAmount,
 * owedAmount), and "Part payment" when the line is less than that (part); a
 * line names its open match's expected amount and what is matched so far
 * (openMatchExpectedAmount, openMatchReceivedAmount), and carries a "Part
 * payment" tag while its order is not paid in full. The sums are exact
 * (@lfoc/domain/decimal.js), never a float.
 *
 * IP-184: a DEBIT may pay a redemption - the money the fund paid out to a
 * payee. An unmatched debit with no open claim is claimed the same way, from
 * its fund's unpaid redemption payouts (the candidates answer kind PAYOUT;
 * payoutOption), approved or rejected by another person, and an automatic
 * match undone with a comment. Its words say paid, not received; a payout
 * paid to several accounts is paid in parts, one debit per account, and a
 * debit that pays nothing (a bank charge) is still cleared by hand.
 */
import { add, cmp, isDecimal, sub } from '@lfoc/domain/decimal.js';

import { formatMoney } from '../../../../format.js';
import { STATUS_KIND, statusHelp } from '../../../../status/statusHelp.js';

/**
 * The four views of the list (design 8, IP-157): the query each sends to GET
 * cash/lines. A finished line - matched and cleared, refunded, cleared by
 * hand, or returned - is never shown with the lines still to do: the screen
 * opens on Outstanding, and the finished lines have their own Cleared view.
 */
export const STATEMENT_FILTERS = Object.freeze([
  // Every line someone still has to act on (cash/sql.js OUTSTANDING).
  { value: 'OUTSTANDING', label: 'Outstanding', query: { outstanding: 'true' } },
  // Open credits, unmatched debits (IP-184) and MANUAL_PENDING (design 6).
  { value: 'UNRECONCILED', label: 'Unreconciled', query: { reconciled: 'false' } },
  { value: 'DEBITS', label: 'Debits', query: { direction: 'DBIT', outstanding: 'true' } },
  // The finished lines.
  { value: 'CLEARED', label: 'Cleared', query: { outstanding: 'false' } },
]);

/** A page of lines: the server's cap (design 6: limit <= 100). */
export const LINES_PAGE = 100;

/** A last note as the list carries it is cut to this many characters (design 6). */
export const LAST_NOTE_CHARS = 120;

/** A note's most characters (catalogue.js LINE_NOTE). */
export const NOTE_MAX = 500;

/**
 * The query of one page of the list.
 *
 * @param {{ filter?: string, fundId?: string|null, cursor?: string|null, limit?: number }} view
 */
export function linesQuery({ filter = 'OUTSTANDING', fundId = null, cursor = null, limit = LINES_PAGE } = {}) {
  const chosen = STATEMENT_FILTERS.find((f) => f.value === filter) ?? STATEMENT_FILTERS[0];
  return { ...chosen.query, fundId: fundId || undefined, cursor: cursor || undefined, limit };
}

/**
 * A list answer's items: the server answers { [key]: [...] } - lines,
 * candidates, notes - and an array alone is taken as it is; anything else
 * is no items, never a crash.
 */
export function itemsOf(answer, key) {
  if (Array.isArray(answer)) return answer;
  const items = answer?.[key];
  return Array.isArray(items) ? items : [];
}

/**
 * The counts an answer carries for its own filter, in words for the panel's
 * hint - or null before they arrive. IP-184: the unmatched debits, once a
 * server counts them.
 */
export function countsText(counts) {
  if (!counts) return null;
  const open = Number(counts.openCredits) || 0;
  const debits = Number(counts.openDebits) || 0;
  const pending = Number(counts.manualPending) || 0;
  return open + ' open credit' + (open === 1 ? '' : 's') + ', '
    + (counts.openDebits === undefined ? '' : debits + ' unmatched debit' + (debits === 1 ? '' : 's') + ', ')
    + pending + ' claim' + (pending === 1 ? '' : 's') + ' awaiting approval';
}

/** One more page under the lines shown: a line already listed (by lineId) is kept once, where it first stood. */
export function appendPage(lines, page) {
  const seen = new Set(lines.map((l) => l.lineId));
  return [...lines, ...(page ?? []).filter((l) => !seen.has(l.lineId))];
}

/** The tab's badge (design 8): the open credits, (IP-184) the unmatched debits and the manual matches waiting, or null before counts arrive. */
export function badgeCount(counts) {
  if (!counts) return null;
  return (Number(counts.openCredits) || 0) + (Number(counts.openDebits) || 0) + (Number(counts.manualPending) || 0);
}

/** The amount as the bank moved it: a debit reads negative (the server sends a positive decimal string and the direction). */
export function signedAmount(line) {
  const amount = String(line?.amount ?? '0');
  if (line?.direction !== 'DBIT' || amount.startsWith('-')) return amount;
  return '-' + amount;
}

/** Uids compare case-insensitively (as the server's maker-checker test). */
export function sameUid(a, b) {
  return Boolean(a) && Boolean(b) && String(a).toLowerCase() === String(b).toLowerCase();
}

/** matching.js openLine: a credit nobody has matched and the bank has not returned. */
export function isOpenCredit(line) {
  return line?.direction === 'CRDT' && line?.matchState === 'UNRECONCILED' && line?.clearanceState !== 'RETURNED';
}

/** IP-184 (payout-match.js openDebit): a debit nobody has matched or claimed - it may pay a redemption. */
export function isOpenDebit(line) {
  return line?.direction === 'DBIT' && line?.matchState === 'UNRECONCILED';
}

/** IP-184: a debit's money went out - a redemption's payout - so its words say paid, not received. */
export const isPayoutLine = (line) => line?.direction === 'DBIT';

/** Whether the Transaction ID cell offers the candidate picker: an open credit, or (IP-184) an unmatched debit, with no open claim on it. */
export function offersClaim(line) {
  return (isOpenCredit(line) || isOpenDebit(line)) && !line.matchId;
}

const MATCH_TAG = Object.freeze({
  AUTO: { label: 'Auto reconciled', colour: 'green' },
  MANUAL_APPROVED: { label: 'Manually reconciled', colour: 'green' },
  MANUAL_PENDING: { label: 'Awaiting approval', colour: 'gold' },
  UNRECONCILED: { label: 'Unreconciled', colour: 'default' },
  REFUNDED: { label: 'Refunded', colour: 'purple' },
  MANUALLY_CLEARED: { label: 'Manually cleared', colour: 'geekblue' },
});

const CLEARANCE_TAG = Object.freeze({
  AWAITING_CLEARANCE: { label: 'Awaiting clearance', colour: 'orange' },
  CLEARED: { label: 'Cleared', colour: 'cyan' },
  RETURNED: { label: 'Returned', colour: 'red' },
});

/** IP-71's ladder, in words and colour. */
const STAGE_TAG = Object.freeze({
  NEW: { label: 'New', colour: 'default' },
  INVESTIGATE: { label: 'Investigate', colour: 'gold' },
  ESCALATE: { label: 'Escalate', colour: 'orange' },
  RETURN_DUE: { label: 'Return due', colour: 'red' },
});

const days = (n) => (Number(n) === 1 ? '1 business day' : String(n) + ' business days');

const lkr = (amount) => 'LKR ' + formatMoney(amount);
const positive = (amount) => isDecimal(amount) && cmp(amount, '0') > 0;

/**
 * IP-176: whether a line pays part of its order, and how much the order
 * still owes. An approved part (AUTO or MANUAL_APPROVED: already in
 * openMatchReceivedAmount) while the order is not paid in full, or a claim
 * waiting (MANUAL_PENDING) that leaves it owing once approved. Null for any
 * other line, and for one whose open match names no expected amount.
 *
 * @returns {{ receivedAmount: string, expectedAmount: string, claimAmount: string|null, owedAfter: string }|null}
 */
export function partPayment(line) {
  const expected = line?.openMatchExpectedAmount;
  if (!isDecimal(expected)) return null;
  const received = isDecimal(line.openMatchReceivedAmount) ? line.openMatchReceivedAmount : '0.00';
  const claim = line.matchState === 'MANUAL_PENDING' && isDecimal(line.openMatchAmount) ? line.openMatchAmount : null;
  if (!claim && line.matchState !== 'AUTO' && line.matchState !== 'MANUAL_APPROVED') return null;
  const after = claim ? add(received, claim) : received;
  if (cmp(after, expected) >= 0) return null;
  return { receivedAmount: received, expectedAmount: expected, claimAmount: claim, owedAfter: sub(expected, after) };
}

/** The Part payment tag: R of A received (IP-184: paid, on a debit), and what is still owed (after the claim, on one waiting). */
function partTag(part, line) {
  if (isPayoutLine(line)) {
    const paid = lkr(part.receivedAmount) + ' of ' + formatMoney(part.expectedAmount) + ' paid';
    return {
      label: 'Part payment',
      colour: 'blue',
      title: part.claimAmount
        ? paid + '. Approving this claim adds ' + lkr(part.claimAmount) + ': ' + lkr(part.owedAfter) + ' of the payout will still be unpaid.'
        : paid + ': ' + lkr(part.owedAfter) + ' of the payout is still unpaid.',
    };
  }
  const received = lkr(part.receivedAmount) + ' of ' + formatMoney(part.expectedAmount) + ' received';
  return {
    label: 'Part payment',
    colour: 'blue',
    title: part.claimAmount
      ? received + '. Approving this claim adds ' + lkr(part.claimAmount) + ': ' + lkr(part.owedAfter) + ' will still be owed, and the transaction waits for the rest.'
      : received + ': the transaction waits for the ' + lkr(part.owedAfter) + ' still owed.',
  };
}

/**
 * The Status cell: the match state (and a part payment's share of its
 * order, IP-176), the clearance (a returned cheque says so, with the bank's
 * date), and an unallocated credit's age and stage - or that its age is
 * unknown, and why.
 *
 * @returns {{ label: string, colour: string, title?: string }[]}
 */
export function statusTags(line) {
  const tags = [];
  // Each tag carries its explanation (status/statusHelp.js) as its title: what
  // the state means and what to do next. Where a code has no words, no title.
  const withHelp = (tag, kind, code) => {
    const help = statusHelp(kind, code);
    return help ? { ...tag, title: help } : tag;
  };
  if (line?.matchState === 'MANUALLY_CLEARED') {
    // IP-157: who cleared it by hand, and why, on the tag.
    const by = line.manuallyClearedByName ?? line.manuallyClearedBy;
    tags.push({ ...MATCH_TAG.MANUALLY_CLEARED, title: [by && 'By ' + by, line.manualClearReason].filter(Boolean).join(': ') || undefined });
  } else if (line?.matchState) {
    tags.push(withHelp(MATCH_TAG[line.matchState] ?? { label: line.matchState, colour: 'default' }, STATUS_KIND.STATEMENT_MATCH, line.matchState));
  }
  const part = partPayment(line);
  if (part) tags.push(partTag(part, line));
  const clearance = CLEARANCE_TAG[line?.clearanceState];
  if (clearance) {
    const explained = withHelp(clearance, STATUS_KIND.STATEMENT_CLEARANCE, line.clearanceState);
    // A returned cheque says when, and the bank's own reason comes first.
    tags.push(line.clearanceState === 'RETURNED' && line.returnedOn
      ? { ...explained, label: 'Returned ' + line.returnedOn, title: [line.returnReason && 'Bank\'s reason: ' + line.returnReason + '.', explained.title].filter(Boolean).join(' ') || undefined }
      : explained);
  }
  if (line?.stage) {
    const stage = withHelp(STAGE_TAG[line.stage] ?? { label: line.stage, colour: 'default' }, STATUS_KIND.STATEMENT_AGE, line.stage);
    tags.push({ ...stage, label: stage.label + ' · ' + days(line.ageBusinessDays ?? 0) });
  } else if (line?.ageUnknown) {
    tags.push({
      label: 'Age unknown',
      colour: 'default',
      title: line.ageUnknown === 'NO_FUND_CONFIG' ? 'Its fund has no fund config today, so the line cannot be aged.' : line.ageUnknown,
    });
  }
  return tags;
}

/**
 * The actions of design 8, by key: the catalogue type, the gate key
 * (SCREEN_RULES.approval.actions), the fields its dialog asks for, the params
 * the code is bound to and the call that sends them with the code.
 */
export const ACTIONS = Object.freeze({
  approveMatch: {
    type: 'MATCH_APPROVE',
    label: 'Approve',
    title: 'Approve this match?',
    fields: [],
    params: (line) => ({ matchId: line.matchId, amount: line.openMatchAmount }),
    send: (api, line, params, stepUp) => api.cash.approve(line.matchId, { amount: params.amount, stepUp }),
    done: (line) => {
      // IP-176: a part leaves its transaction waiting for the rest.
      const part = partPayment(line);
      if (part && isPayoutLine(line)) {
        return 'Part of the payout approved' + (line.transactionRef ? ' against ' + line.transactionRef : '') + ': ' + lkr(part.owedAfter) + ' of it is still to be paid.';
      }
      if (part) return 'Part payment approved' + (line.transactionRef ? ' against ' + line.transactionRef : '') + ': the transaction waits for the ' + lkr(part.owedAfter) + ' still owed.';
      // IP-184: a debit's approval confirms the redemption's payout.
      if (isPayoutLine(line)) return line.transactionRef ? 'Payout reconciled against ' + line.transactionRef + '.' : 'Payout reconciled.';
      return line.transactionRef ? 'Reconciled against ' + line.transactionRef + '.' : 'Match approved.';
    },
  },
  rejectMatch: {
    type: 'MATCH_REJECT',
    label: 'Reject',
    title: 'Reject this match?',
    danger: true,
    fields: ['reason'],
    params: (line, values) => ({ matchId: line.matchId, reason: clean(values.reason) }),
    send: (api, line, params, stepUp) => api.cash.reject(line.matchId, { reason: params.reason, stepUp }),
    done: () => 'Match rejected: the line is open again.',
  },
  clear: {
    type: 'LINE_CLEAR',
    label: 'Clear',
    title: 'Record the bank\'s clearance',
    fields: ['clearedOn', 'bankReference', 'reason'],
    params: (line, values) => ({ lineId: line.lineId, clearedOn: values.clearedOn, bankReference: clean(values.bankReference), reason: clean(values.reason) }),
    send: (api, line, { clearedOn, bankReference, reason }, stepUp) => api.cash.clear(line.lineId, { clearedOn, bankReference, reason, stepUp }),
    done: () => 'Clearance recorded.',
  },
  returnLine: {
    type: 'LINE_RETURN',
    label: 'Return',
    title: 'Return this cheque?',
    danger: true,
    fields: ['returnedOn', 'reason'],
    params: (line, values) => ({ lineId: line.lineId, returnedOn: values.returnedOn, reason: clean(values.reason) }),
    send: (api, line, { returnedOn, reason }, stepUp) => api.cash.returnLine(line.lineId, { returnedOn, reason, stepUp }),
    done: (line) => (line?.matchState === 'UNRECONCILED' ? 'Cheque returned: no transaction waited on it.' : 'Cheque returned: its transaction waits for funds again.'),
  },
  unreconcile: {
    type: 'LINE_UNRECONCILE',
    label: 'Unreconcile',
    title: 'Unreconcile this transaction?',
    outlined: true,
    fields: ['reason'],
    labels: { reason: 'Comment (why the match is wrong)' },
    params: (line, values) => ({ lineId: line.lineId, reason: clean(values.reason) }),
    send: (api, line, { reason }, stepUp) => api.cash.unreconcile(line.lineId, { reason, stepUp }),
    done: (line) => (isPayoutLine(line)
      ? 'Unreconciled: the debit is unmatched again and the redemption\'s payout is unpaid until it is matched.'
      : 'Unreconciled: the credit is unallocated again and its transaction waits for its money.'),
  },
  refund: {
    type: 'RECEIPT_REFUND',
    label: 'Clear',
    title: 'Clear this receipt?',
    outlined: true,
    fields: ['refundReference', 'reason'],
    params: (line, values) => ({ lineId: line.lineId, refundReference: clean(values.refundReference), reason: clean(values.reason) }),
    send: (api, line, { refundReference, reason }, stepUp) => api.cash.refund(line.lineId, { refundReference, reason, stepUp }),
    done: () => 'Refund recorded.',
  },
  manualClear: {
    type: 'LINE_MANUAL_CLEAR',
    label: 'Clear',
    title: 'Clear this line by hand?',
    outlined: true,
    fields: ['reason'],
    labels: { reason: 'Reason (why the line needs nothing more)' },
    params: (line, values) => ({ lineId: line.lineId, reason: clean(values.reason) }),
    send: (api, line, { reason }, stepUp) => api.cash.manualClear(line.lineId, { reason, stepUp }),
    done: () => 'Cleared: the line is on the Cleared view now.',
  },
});

/**
 * IP-157: a left-over line the person may clear by hand - unmatched with no
 * claim open, neither returned nor still in clearing (the server's own rule,
 * cash/manual-clear.js manualClearRefusal), and not a credit the Refund
 * (offered as Clear) already ends.
 */
export function offersManualClear(line) {
  return line?.matchState === 'UNRECONCILED' && !line.matchId
    && line.clearanceState !== 'RETURNED' && line.clearanceState !== 'AWAITING_CLEARANCE'
    && line.refundable !== true;
}

/** The catalogue trims text before binding a code to it (catalogue.js text); the page sends what it binds. */
function clean(value) {
  return typeof value === 'string' ? value.trim() : value;
}

/**
 * The actions a line offers this person, in the column's order: each with
 * its key and, when the person's roles do not allow it or it cannot be sent
 * yet, why (the button shows disabled, with the reason as its title).
 *
 * @param {object} line
 * @param {{ me?: string|null, gates: Record<string, boolean> }} who gates keyed as ACTIONS
 * @returns {{ key: string, label: string, danger?: boolean, outlined?: boolean, why: string|null }[]}
 */
export function lineActions(line, { me = null, gates = {} } = {}) {
  const offered = [];
  const offer = (key, why = null) => {
    const action = ACTIONS[key];
    offered.push({
      key,
      label: action.label,
      danger: action.danger ?? false,
      outlined: action.outlined ?? false,
      why: gates[key] ? why : 'Your roles do not allow this.',
    });
  };
  if (line?.matchState === 'MANUAL_PENDING' && line.matchId && !sameUid(line.proposedBy, me)) {
    offer('approveMatch', line.openMatchAmount ? null : 'The match\'s amount is not known: refresh the list.');
    offer('rejectMatch');
  }
  const clearing = line?.direction === 'CRDT' && line?.clearanceState === 'AWAITING_CLEARANCE';
  if (clearing) offer('clear');
  const unallocated = line?.matchState === 'UNRECONCILED' && !line.matchId;
  if (clearing && (line.matchState === 'AUTO' || line.matchState === 'MANUAL_APPROVED' || unallocated)) offer('returnLine');
  // IP-196: an automatic match or an approved manual one, until its transaction is approved - while the server says it
  // can still be undone - a credit's, or (IP-184) a debit's match to a redemption's payout (not a payout file's confirmation).
  const undoable = line?.direction === 'CRDT' || line?.openMatchMovementType === 'RED_PAYOUT';
  if (undoable && (line.matchState === 'AUTO' || line.matchState === 'MANUAL_APPROVED') && line.unreconcileBlockedBy === null) offer('unreconcile');
  if (line?.refundable === true) offer('refund');
  if (offersManualClear(line)) offer('manualClear');
  return offered;
}

/** Why a MANUAL_PENDING line offers this person no decision: they claimed it. Null otherwise. */
export function ownClaimNote(line, me) {
  return line?.matchState === 'MANUAL_PENDING' && sameUid(line.proposedBy, me) ? 'You claimed this match: another person approves or rejects it.' : null;
}

/** How much of a candidate's amount is in (IP-184: paid, for a payout), or claimed and waiting for a checker - null while neither. */
function paidText(c, { payout = false } = {}) {
  const received = positive(c.receivedAmount);
  const pending = positive(c.pendingAmount);
  if (!received && !pending) return null;
  const of = ' of ' + formatMoney(c.amount);
  return [received && lkr(c.receivedAmount) + of + (payout ? ' paid' : ' received'), pending && lkr(c.pendingAmount) + (received ? '' : of) + ' awaiting approval']
    .filter(Boolean).join(', ');
}

/**
 * A candidate (GET cash/lines/{id}/candidates) as a picker option: its
 * transaction ref, holder and what it is still owed (owedAmount: its amount
 * less what is received and what is claimed; its amount from a server that
 * does not say) - with how much of its amount is in or claimed, once any is
 * (IP-176) - its S1/S3 tier, and "Part payment" when the line is less than
 * it is owed: approved, the transaction waits for the rest. Every candidate
 * can be chosen now that a short line is a part; one the server still marks
 * not selectable is listed, disabled.
 */
export function candidateOption(c) {
  const paid = paidText(c);
  const owed = 'LKR ' + formatMoney(c.owedAmount ?? c.amount) + (paid ? ' owed' : '');
  const title = [
    paid && paid + '; ' + lkr(c.owedAmount ?? c.amount) + ' still owed.',
    c.part && 'Part payment: this line is less than the transaction is owed, so once approved it waits for the rest.',
  ].filter(Boolean).join(' ');
  return {
    value: c.movementId,
    // IP-202: the strategy activation code after the transaction's reference.
    label: [c.transactionRef ?? c.orderId, c.strategyActivationCode, c.holderNo, owed, paid, c.tier, c.part ? 'Part payment' : null].filter(Boolean).join(' · '),
    disabled: c.selectable === false,
    title: title || undefined,
  };
}

/**
 * IP-184: an unpaid redemption payout (GET cash/lines/{id}/candidates of a
 * debit, kind PAYOUT) as a picker option: its transaction ref, holder, what
 * it still owes (and how much of its net is paid or claimed, once any is),
 * its dealing date and tier (S1 the debit carries its reference, S3 its
 * amount is one the payout may be paid), and "Part payment" when the debit
 * pays only part of it - one account of several, the rest paid by another
 * debit. One the debit is more than (selectable false) is listed, disabled:
 * a claim would be refused.
 */
export function payoutOption(c) {
  const paid = paidText(c, { payout: true });
  const owed = 'LKR ' + formatMoney(c.owedAmount ?? c.amount) + (paid ? ' owed' : '');
  const title = [
    paid && paid + '; ' + lkr(c.owedAmount ?? c.amount) + ' still to be paid.',
    c.part && 'Part payment: this debit is less than the payout still owes, so once approved it waits for the rest.',
    c.selectable === false && 'This debit is more than the payout still owes: it cannot be claimed for it.',
  ].filter(Boolean).join(' ');
  return {
    value: c.movementId,
    // IP-202: the strategy activation code after the transaction's reference.
    label: [c.transactionRef ?? c.orderId, c.strategyActivationCode, c.holderNo, owed, paid, c.dealingDate && 'dealt ' + c.dealingDate, c.tier, c.part ? 'Part payment' : null]
      .filter(Boolean).join(' · '),
    disabled: c.selectable === false,
    title: title || undefined,
  };
}

/** The list's line once a note is added to it: one more note, and the note as its last (cut as the server cuts it). */
export function withNote(line, note) {
  const text = String(note ?? '').trim();
  return {
    ...line,
    noteCount: (Number(line.noteCount) || 0) + 1,
    lastNote: text.length > LAST_NOTE_CHARS ? text.slice(0, LAST_NOTE_CHARS) : text,
  };
}

/**
 * READ-3 / CON-4: a line's notes come newest first, a page at a time
 * (cash/notes.js lineNotes - 50 by default, with nextCursor while older ones
 * remain). The older page's query: the server's own cursor.
 */
export function notesQuery(cursor = null) {
  return { cursor: cursor || undefined };
}

/** An older page of notes under those shown, a note (by noteId) listed once. */
export function appendNotes(notes, page) {
  const shown = notes ?? [];
  const seen = new Set(shown.map((n) => n.noteId));
  return [...shown, ...(page ?? []).filter((n) => !seen.has(n.noteId))];
}

/**
 * What the popover says while older notes are not loaded yet, or null once
 * the server has none left (no nextCursor). total is the list's noteCount;
 * read earlier than the notes, it is named only when it is more than shown.
 */
export function olderNotesText({ shown, total, more }) {
  if (!more) return null;
  return 'The newest ' + shown + (Number(total) > shown ? ' of ' + total : '') + ' notes are shown: older ones are not loaded yet.';
}

/**
 * The fund filter's options: the funds the register lists, when the viewer
 * may read it, and every fund a listed line belongs to, so the filter always
 * offers the funds on the page.
 *
 * @param {{ fundId: string, name?: string }[]} funds
 * @param {string[]} seen fund ids met in the lines
 */
export function fundOptions(funds = [], seen = []) {
  const options = new Map();
  for (const f of funds) options.set(f.fundId, { value: f.fundId, label: f.name ? f.fundId + ' · ' + f.name : f.fundId });
  for (const fundId of seen) if (fundId && !options.has(fundId)) options.set(fundId, { value: fundId, label: fundId });
  return [...options.values()].sort((a, b) => a.value.localeCompare(b.value));
}

/** The latest a bank's date may be (assertBankDate): today in Colombo; the earliest is the line's value date. */
export function bankDateAllowed(line, ymd, today) {
  return Boolean(ymd) && ymd >= (line?.valueDate ?? '') && ymd <= today;
}

/** A refusal that means the line moved under the person: the list is read again. */
export function isStale(error) {
  return error?.status === 409 || error?.status === 404;
}
